/**
 * 業界別使い方ガイド詳細ページ
 *
 * 機能:
 * - 業界別の主な機能紹介
 * - 活用例の表示
 * - 用語設定の表示
 * - デモアカウント情報の表示
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Button,
  Space,
  List,
  Result,
  Tag,
  Divider,
  Row,
  Col,
} from 'antd';
import { LoginOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { PublicPageLayout } from '../components/public/PublicPageLayout';

const { Title, Paragraph, Text } = Typography;

/**
 * 業界別のガイドコンテンツ
 */
const guideContent: Record<
  string,
  {
    title: string;
    subtitle: string;
    overview: string;
    features: Array<{ title: string; description: string; details: string[] }>;
    useCases: Array<{ title: string; situation: string; solution: string; effect: string }>;
    demoAccounts: { type: string; email: string; password: string; role: string }[];
  }
> = {
  factory: {
    title: '工場向け一元帳システム',
    subtitle: '製造業の設備保全業務を効率化',
    overview:
      '製造現場における設備管理、部品・消耗品在庫管理、予防保全を一元化。生産ラインの安定稼働と計画的なメンテナンスを実現します。',
    features: [
      {
        title: '設備の階層管理と健全度監視',
        description: '生産ラインごとに設備を階層的に管理し、健全度を可視化',
        details: [
          '設備ツリー表示で親子関係を一目で把握',
          '健全度予測による更新時期の提案',
          '重要設備の優先的な監視',
          '設備ごとの作業履歴・部品・消耗品交換履歴',
        ],
      },
      {
        title: '部品・消耗品在庫管理と自動発注',
        description: '交換部品・消耗品の在庫状況をリアルタイムで把握',
        details: [
          '部品・消耗品ごとの在庫数・最低在庫数の設定',
          '在庫切れアラート機能',
          '発注履歴と納期管理',
          '設備との紐付けで必要部品・消耗品を即座に確認',
        ],
      },
      {
        title: '予防保全スケジューリング',
        description: '計画的なメンテナンスでトラブルを未然に防止',
        details: [
          '保全カレンダー形式で保全予定を可視化',
          '担当者へのタスク割り当て',
          '作業テンプレートで標準化',
          '実績記録と写真添付',
        ],
      },
    ],
    useCases: [
      {
        title: '生産ラインの設備管理',
        situation: '複数の生産ラインで設備情報がExcelと紙でバラバラ。設備の状態把握に時間がかかる。',
        solution: '設備を階層的に登録し、各設備の健全度・作業履歴を一元管理。',
        effect: '設備情報の検索時間が30分→数秒に短縮。トラブル時の対応が迅速化。',
      },
      {
        title: '予防保全による故障削減',
        situation: '突発的な故障対応に追われ、計画的なメンテナンスができていない。',
        solution: '健全度予測機能で更新時期を提案。保全カレンダーで計画的に実施。',
        effect: '突発故障が年間30%削減。生産停止時間が大幅に減少。',
      },
      {
        title: '部品・消耗品在庫の最適化',
        situation: '部品・消耗品の在庫管理が曖昧で、必要な時に在庫切れが発生。',
        solution: '部品・消耗品ごとの在庫数を管理し、最低在庫数でアラート。発注履歴も記録。',
        effect: '緊急発注が50%削減。在庫コストを20%削減。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-factory@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  building_maintenance: {
    title: 'ビルメンテナンス向け一元帳システム',
    subtitle: 'ビル設備の保守管理を効率化',
    overview:
      '複数ビルの設備管理、定期点検、部材発注を一元化。法定点検の漏れ防止と、効率的なメンテナンス業務を実現します。',
    features: [
      {
        title: '建物別設備管理',
        description: '複数ビルの設備情報を建物ごとに整理',
        details: [
          '建物（エリア）階層での設備管理',
          '空調・電気・給排水など設備区分での分類',
          '各設備の点検履歴と状態管理',
          'ビルごとの設備一覧表示',
        ],
      },
      {
        title: '定期点検スケジュール管理',
        description: '法定点検を含む定期点検を確実に実施',
        details: [
          '点検スケジュールの保全カレンダー表示',
          '法定点検の期限管理とアラート',
          '点検結果の記録と写真添付',
          '取引先への作業依頼管理',
        ],
      },
      {
        title: '部材発注と取引先管理',
        description: '交換部材の発注と取引先情報を一元管理',
        details: [
          '部材の在庫管理と発注履歴',
          '取引先情報の登録と連絡先管理',
          '発注から納品までの進捗管理',
          '部材使用履歴の記録',
        ],
      },
    ],
    useCases: [
      {
        title: '複数ビルの一元管理',
        situation: '10棟のビルを管理しているが、各ビルで別々のExcelファイルで管理。情報の集約に時間がかかる。',
        solution: 'システムで全ビルの設備情報を一元管理。建物ごとのフィルタリングで必要な情報に即座にアクセス。',
        effect: '情報検索時間が80%削減。管理業務の工数が半減。',
      },
      {
        title: '法定点検の確実な実施',
        situation: '法定点検の期限管理が大変で、うっかり忘れそうになることがある。',
        solution: '法定点検をスケジュール登録し、期限前にアラート通知。点検結果も記録。',
        effect: '法定点検の漏れゼロを実現。点検記録の管理も効率化。',
      },
      {
        title: '取引先との情報共有',
        situation: '複数の取引先に作業を依頼しているが、連絡先や過去の作業内容がバラバラ。',
        solution: '取引先情報を登録し、作業依頼履歴を記録。連絡先も一元管理。',
        effect: '取引先への連絡が迅速化。過去の作業内容を即座に確認可能。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-building_maintenance@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  educational_institution: {
    title: '学校法人向け一元帳システム',
    subtitle: '学校施設の設備・備品管理を効率化',
    overview:
      '校舎、体育館、実験室などの施設設備と備品を一元管理。法定点検の確実な実施と、修繕履歴の記録で施設管理業務を効率化します。',
    features: [
      {
        title: '施設設備の管理',
        description: '校舎・体育館など施設ごとに設備を管理',
        details: [
          '建物・フロア・教室単位での施設設備管理',
          '空調、電気、給排水、防災設備の一元管理',
          '設備種別（空調、電気等）での分類',
          '法定点検対象設備の明確化',
        ],
      },
      {
        title: '備品台帳管理',
        description: '学校で使用する備品を台帳として管理',
        details: [
          '備品の購入履歴と配置場所の記録',
          '備品区分（教材、事務用品等）での分類',
          '備品の使用状況と在庫管理',
          '購入予算の管理',
        ],
      },
      {
        title: '法定点検と修繕管理',
        description: '法定点検の確実な実施と修繕履歴の記録',
        details: [
          '消防設備点検、電気設備点検などの期限管理',
          '点検結果の記録と是正措置の管理',
          '修繕履歴の蓄積',
          '業者情報と作業実績の管理',
        ],
      },
    ],
    useCases: [
      {
        title: '施設設備の効率的な管理',
        situation: '複数の校舎・体育館の設備情報が各施設でバラバラに管理され、全体把握が困難。',
        solution: '全施設の設備情報をシステムで一元管理。施設別、設備種別でフィルタリング可能。',
        effect: '設備情報の検索時間が90%削減。施設間の情報共有が円滑に。',
      },
      {
        title: '法定点検の確実な実施',
        situation: '消防設備点検、電気設備点検など複数の法定点検があり、期限管理が大変。',
        solution: '法定点検をスケジュール登録し、期限前にアラート。点検結果も記録。',
        effect: '法定点検の漏れゼロを達成。点検記録の保管も確実に。',
      },
      {
        title: '備品購入の予算管理',
        situation: '備品の購入履歴が不明確で、予算執行状況の把握が困難。',
        solution: '備品の購入履歴を記録し、年度ごとの予算執行状況を可視化。',
        effect: '予算管理の透明性が向上。無駄な購入を削減。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-educational_institution@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  municipality: {
    title: '自治体向け一元帳システム',
    subtitle: '公共施設の適切な管理と透明性の確保',
    overview:
      '公共施設の設備管理、資材管理、点検保守業務を一元化。透明性の高い施設管理で、住民への説明責任を果たします。',
    features: [
      {
        title: '公共施設の一元管理',
        description: '市内の公共施設を施設種別ごとに管理',
        details: [
          '庁舎、学校、体育館、図書館などの施設管理',
          '施設区分（行政、教育、文化等）での分類',
          '施設ごとの設備一覧と状態管理',
          '複数施設の横断的な情報把握',
        ],
      },
      {
        title: '資材管理と予算執行',
        description: '修繕資材の管理と予算執行状況の可視化',
        details: [
          '資材の在庫管理と発注履歴',
          '予算執行状況のリアルタイム把握',
          '年度ごとの支出分析',
          '資材区分での管理',
        ],
      },
      {
        title: '点検・保守の記録管理',
        description: '点検保守業務の履歴を確実に記録',
        details: [
          '定期点検・法定点検のスケジュール管理',
          '点検結果と是正措置の記録',
          '業者への作業依頼と実績管理',
          '業務履歴の長期保存',
        ],
      },
    ],
    useCases: [
      {
        title: '公共施設の効率的な管理',
        situation: '市内50以上の公共施設の設備情報が各施設で個別管理され、全体把握が困難。',
        solution: '全施設の設備情報をシステムで一元管理。施設種別、設備種別で横断的に把握。',
        effect: '施設管理業務の効率が40%向上。情報の透明性が確保される。',
      },
      {
        title: '法定点検の確実な実施',
        situation: '多数の施設で法定点検の期限管理が複雑。漏れのリスクがある。',
        solution: '法定点検を保全カレンダー登録し、期限前にアラート。点検結果も記録して監査に対応。',
        effect: '法定点検の100%実施を達成。監査対応もスムーズに。',
      },
      {
        title: '透明性の高い予算執行',
        situation: '修繕費の予算執行状況が不透明で、議会への説明資料作成に時間がかかる。',
        solution: '資材購入と修繕履歴を記録し、予算執行状況をリアルタイムで可視化。',
        effect: '議会資料の作成時間が70%削減。透明性の高い施設管理を実現。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-municipality@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  hotel: {
    title: 'ホテル向け一元帳システム',
    subtitle: 'ホテル設備の総合管理と快適な宿泊環境の維持',
    overview:
      '客室設備、宴会設備、厨房設備など多岐にわたるホテル設備を一元管理。リネン・アメニティの在庫管理と、計画的なメンテナンスでゲストに快適な滞在を提供します。',
    features: [
      {
        title: '客室設備の一元管理',
        description: '全客室の設備状態を把握し、快適性を維持',
        details: [
          '客室ごとのエアコン、テレビ、冷蔵庫等の管理',
          '客室階層での設備一覧表示',
          '不具合報告と即座の対応',
          '客室メンテナンス履歴の記録',
        ],
      },
      {
        title: '宴会・厨房設備管理',
        description: '宴会場の音響照明、厨房機器の保守管理',
        details: [
          '宴会設備（音響、照明、映像機器）の点検管理',
          '業務用厨房機器の定期メンテナンス',
          '給湯設備の安定稼働管理',
          '設備トラブルの迅速な対応',
        ],
      },
      {
        title: 'リネン・アメニティ在庫管理',
        description: 'タオル、アメニティグッズの在庫を最適化',
        details: [
          'リネン類の在庫数管理',
          'アメニティグッズの発注管理',
          '使用量の把握と発注計画',
          '品質管理と交換時期の記録',
        ],
      },
    ],
    useCases: [
      {
        title: '客室設備の迅速な対応',
        situation: '客室のエアコン故障など、設備トラブルの対応に時間がかかる。',
        solution: '客室ごとの設備情報と点検履歴を記録。過去の対応方法を即座に確認。',
        effect: 'トラブル対応時間が50%短縮。ゲスト満足度が向上。',
      },
      {
        title: '宴会設備の確実な準備',
        situation: '宴会前の設備点検が属人的で、チェック漏れのリスクがある。',
        solution: '宴会設備の点検チェックリストを登録し、確実に実施。履歴も記録。',
        effect: '宴会時のトラブルゼロを実現。準備時間も効率化。',
      },
      {
        title: 'リネン在庫の最適化',
        situation: 'タオル等のリネン在庫管理が曖昧で、発注タイミングが分からない。',
        solution: 'リネン使用量を記録し、在庫数を可視化。最適な発注計画を立案。',
        effect: '在庫切れゼロ。過剰在庫を30%削減。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-hotel@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  commercial_facility: {
    title: '商業施設向け一元帳システム',
    subtitle: '商業施設の設備管理と安全で快適な買い物環境の維持',
    overview:
      'ショッピングモールや商業ビルの共用部設備、エスカレーター、駐車場設備を一元管理。テナント対応とお客様の安全・快適性を両立します。',
    features: [
      {
        title: '共用部設備の一元管理',
        description: '館内共用部の空調・照明・トイレ設備を統合管理',
        details: [
          'フロアごとの空調・照明設備管理',
          '共用部トイレ設備の点検履歴',
          '照明器具の交換計画',
          '共用部消耗品（トイレットペーパー等）の在庫管理',
        ],
      },
      {
        title: 'エスカレーター・駐車場管理',
        description: 'お客様の動線設備を確実に稼働',
        details: [
          'エスカレーター・エレベーターの法定点検管理',
          '機械式駐車場の保守点検',
          '設備トラブル時の迅速な対応',
          '交換部品・消耗品の在庫管理',
        ],
      },
      {
        title: 'テナント対応管理',
        description: 'テナントからの要望に迅速に対応',
        details: [
          'テナント要望の受付と対応履歴',
          'テナントエリアの設備管理',
          '作業実施の記録と報告',
          '業者手配と作業進捗管理',
        ],
      },
    ],
    useCases: [
      {
        title: '共用部清掃の効率化',
        situation: '館内共用部の清掃状況が把握しづらく、清掃用品の発注も属人的。',
        solution: '清掃スケジュールを管理し、清掃用品の在庫も可視化。発注計画を立案。',
        effect: '清掃品質が向上。消耗品コストを20%削減。',
      },
      {
        title: 'エスカレーター点検の確実な実施',
        situation: '多数のエスカレーターの法定点検管理が複雑。点検漏れのリスク。',
        solution: '全エスカレーターの点検スケジュールを管理。期限前にアラート通知。',
        effect: '法定点検100%実施。お客様の安全を確保。',
      },
      {
        title: 'テナント対応の迅速化',
        situation: 'テナントからの設備要望への対応が遅れがち。履歴も不明確。',
        solution: 'テナント要望を記録し、対応状況を可視化。過去の対応も即座に確認。',
        effect: 'テナント満足度が向上。対応時間が40%短縮。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-commercial_facility@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
  nursing_home: {
    title: '介護施設向け一元帳システム',
    subtitle: '介護施設の設備管理と安全で衛生的な環境の維持',
    overview:
      '介護ベッド、医療設備、特殊浴槽など介護施設特有の設備を一元管理。衛生用品の在庫管理と、確実な点検で入居者の安全と快適性を確保します。',
    features: [
      {
        title: '介護・医療設備の管理',
        description: '介護ベッド、車椅子、医療機器の保守管理',
        details: [
          '介護ベッド、車椅子、リフトの点検管理',
          '吸引器、酸素供給装置の定期点検',
          'ナースコールシステムの動作確認',
          '設備トラブル時の迅速な対応',
        ],
      },
      {
        title: '衛生管理と消耗品管理',
        description: '衛生用品・医療消耗品の在庫を確実に管理',
        details: [
          '手袋、おむつ等の衛生用品の在庫管理',
          '消毒液、ガーゼ等の医療消耗品管理',
          '使用量の記録と発注計画',
          '期限管理（消費期限のある物品）',
        ],
      },
      {
        title: '浴室・給湯設備の保守',
        description: '特殊浴槽・給湯設備の安全な稼働を確保',
        details: [
          '特殊浴槽の定期点検と清掃記録',
          '給湯ボイラーの保守管理',
          '浴室設備の安全チェック',
          '温度管理と衛生管理',
        ],
      },
    ],
    useCases: [
      {
        title: '介護設備の確実な点検',
        situation: '介護ベッド、リフト等の点検が属人的で、点検漏れのリスクがある。',
        solution: '介護設備の点検スケジュールを管理し、確実に実施。点検結果も記録。',
        effect: '設備トラブルによる事故ゼロ。入居者の安全を確保。',
      },
      {
        title: '衛生用品の在庫最適化',
        situation: '手袋、おむつ等の在庫管理が曖昧で、在庫切れや過剰在庫が発生。',
        solution: '衛生用品の使用量を記録し、在庫数を可視化。最適な発注計画。',
        effect: '在庫切れゼロ。在庫コストを25%削減。',
      },
      {
        title: '衛生管理の徹底',
        situation: '消毒・除菌作業の実施記録が不明確。衛生管理の証明が困難。',
        solution: '消毒・除菌作業をスケジュール管理し、実施記録を確実に保存。',
        effect: '衛生管理の透明性が向上。監査対応もスムーズに。',
      },
    ],
    demoAccounts: [
      {
        type: '閲覧用',
        email: 'demo-viewer-nursing_home@example.com',
        password: 'demo-viewer-2024',
        role: 'viewer',
      },
    ],
  },
};

export default function IndustryGuideDetailPage() {
  const { industryCode } = useParams<{ industryCode: string }>();
  const navigate = useNavigate();

  const content = industryCode ? guideContent[industryCode] : undefined;

  if (!industryCode || !content) {
    return (
      <PublicPageLayout title="業界別ガイド" subtitle="業界の詳細情報">
        <Result
          status="404"
          title="業界が見つかりません"
          subTitle="指定された業界は存在しません"
          extra={
            <Button type="primary" onClick={() => navigate('/industry-guides')}>
              業界一覧に戻る
            </Button>
          }
        />
      </PublicPageLayout>
    );
  }

  return (
    <PublicPageLayout title={content.title} subtitle={content.subtitle}>
      {/* 戻るボタン */}
      <div style={{ marginBottom: '32px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/industry-guides')}>
          業界一覧に戻る
        </Button>
      </div>

      {/* 概要 */}
      <Card style={{ marginBottom: '32px', background: '#f0f5ff', borderColor: '#adc6ff' }}>
        <Paragraph style={{ fontSize: '16px', marginBottom: 0, lineHeight: '1.8' }}>
          {content.overview}
        </Paragraph>
      </Card>

      {/* 主な機能（詳細版） */}
      <Title level={3} style={{ marginTop: '48px', marginBottom: '24px' }}>
        主な機能
      </Title>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {content.features.map((feature, index) => (
          <Card key={index} title={feature.title} style={{ borderLeft: '4px solid #1890ff' }}>
            <Paragraph style={{ fontSize: '15px', color: '#595959', marginBottom: '16px' }}>
              {feature.description}
            </Paragraph>
            <List
              dataSource={feature.details}
              renderItem={(detail) => (
                <List.Item style={{ padding: '8px 0' }}>
                  <Space>
                    <span style={{ color: '#52c41a', fontSize: '16px' }}>✓</span>
                    <Text>{detail}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        ))}
      </Space>

      {/* 活用例（詳細版） */}
      <Title level={3} style={{ marginTop: '48px', marginBottom: '24px' }}>
        活用例
      </Title>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {content.useCases.map((useCase, index) => (
          <Card
            key={index}
            title={`事例 ${index + 1}: ${useCase.title}`}
            style={{ background: '#fafafa' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Tag color="red">課題</Tag>
                <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>{useCase.situation}</Paragraph>
              </div>
              <div>
                <Tag color="blue">解決策</Tag>
                <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>{useCase.solution}</Paragraph>
              </div>
              <div>
                <Tag color="green">効果</Tag>
                <Paragraph style={{ marginTop: '8px', marginBottom: 0, fontWeight: 'bold' }}>
                  {useCase.effect}
                </Paragraph>
              </div>
            </Space>
          </Card>
        ))}
      </Space>

      <Divider />

      {/* デモアカウント情報 */}
      <Card
        title={
          <Space>
            <LoginOutlined />
            <span>デモアカウントで体験</span>
          </Space>
        }
        style={{
          background: '#f6ffed',
          borderColor: '#b7eb8f',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Paragraph style={{ marginBottom: '8px' }}>
            以下のアカウントで実際の画面をご体験いただけます。
            <br />
            <strong>※デモアカウントは閲覧専用です。データの変更はできません。</strong>
          </Paragraph>

          <Row gutter={[16, 16]}>
            {content.demoAccounts.map((account, index) => (
              <Col xs={24} md={12} key={index}>
                <Card
                  size="small"
                  title={
                    <Space>
                      <Tag color={account.type === '閲覧用' ? 'blue' : 'green'}>
                        {account.role}
                      </Tag>
                      <span>{account.type}アカウント</span>
                    </Space>
                  }
                  style={{ background: '#ffffff' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">メールアドレス:</Text>
                      <br />
                      <Text code copyable style={{ fontSize: '13px' }}>
                        {account.email}
                      </Text>
                    </div>
                    <div>
                      <Text type="secondary">パスワード:</Text>
                      <br />
                      <Text code copyable style={{ fontSize: '13px' }}>
                        {account.password}
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Button
              type="primary"
              icon={<LoginOutlined />}
              size="large"
              onClick={() =>
                navigate('/login', {
                  state: {
                    demoAccount: {
                      email: content.demoAccounts[0].email,
                      password: content.demoAccounts[0].password,
                    },
                  },
                })
              }
            >
              デモアカウントでログイン
            </Button>
          </div>
        </Space>
      </Card>
    </PublicPageLayout>
  );
}
